import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "../api/client";
import { Modal } from "./Modal";
import { ConfirmButton } from "./ConfirmButton";
import { formatRelativeTime } from "../util/date";

interface ArchivedCardsProps {
  boardId: string;
  canEdit: boolean;
  onClose: () => void;
}

/** Board archive: restore archived cards, or delete them permanently. */
export function ArchivedCards({ boardId, canEdit, onClose }: ArchivedCardsProps) {
  const qc = useQueryClient();
  const archivedQ = useQuery({
    queryKey: ["archived-cards", boardId],
    queryFn: () => api.boards.archivedCards(boardId),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["archived-cards", boardId] });
    qc.invalidateQueries({ queryKey: ["board", boardId] });
  };
  const restore = useMutation({
    mutationFn: (id: string) => api.cards.unarchive(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.cards.delete(id),
    onSuccess: invalidate,
  });

  const cards = archivedQ.data ?? [];

  return (
    <Modal onClose={onClose} ariaLabel="Archived cards">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Archived cards</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        {archivedQ.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : cards.length === 0 ? (
          <p className="text-sm text-slate-500">
            No archived cards. Archive a card from its actions menu instead of
            deleting it, and it will wait here.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {cards.map((c) => (
              <li key={c.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-800">
                    {c.title}
                  </div>
                  <div className="text-xs text-slate-500">
                    in {c.listTitle}
                    {c.archivedAt &&
                      ` · archived ${formatRelativeTime(c.archivedAt)}`}
                  </div>
                </div>
                {canEdit && (
                  <>
                    <button
                      type="button"
                      onClick={() => restore.mutate(c.id)}
                      className="rounded bg-slate-100 px-2.5 py-1 text-sm text-slate-700 hover:bg-slate-200"
                    >
                      Restore
                    </button>
                    <ConfirmButton
                      onConfirm={() => remove.mutate(c.id)}
                      title="Delete permanently"
                      className="rounded px-2 py-1 text-sm text-slate-400 hover:bg-red-50 hover:text-red-600"
                      confirmClassName="rounded bg-red-600 px-2 py-1 text-sm text-white"
                      confirmLabel="Delete forever?"
                    >
                      Delete
                    </ConfirmButton>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
